'use client';
import React, { useState, useCallback } from 'react';

interface TextStats {
  words: number;
  characters: number;
  charactersNoSpaces: number;
  sentences: number;
  paragraphs: number;
  readingTime: string;
}

function analyzeText(text: string): TextStats {
  const trimmed = text.trim();
  const words = trimmed ? trimmed.split(/\s+/).filter(Boolean).length : 0;
  const characters = text.length;
  const charactersNoSpaces = text.replace(/\s/g, '').length;
  const sentences = trimmed ? trimmed.split(/[.!?]+/).filter((s) => s.trim().length > 0).length : 0;
  const paragraphs = trimmed ? trimmed.split(/\n\s*\n/).filter((p) => p.trim().length > 0).length : 0;
  const minutes = Math.ceil(words / 200);
  const readingTime = minutes < 1 ? '< 1 min' : `${minutes} min`;
  return { words, characters, charactersNoSpaces, sentences, paragraphs, readingTime };
}

export default function WordCounterTool() {
  const [text, setText] = useState('');
  const stats = analyzeText(text);

  const handleClear = () => setText('');
  const handleCopy = () => navigator.clipboard.writeText(text);

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="font-semibold text-text-primary text-sm">Your Text</label>
          <div className="flex gap-2">
            <button onClick={handleCopy} className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-brand-blue hover:bg-blue-100 transition-colors font-medium">Copy</button>
            <button onClick={handleClear} className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 text-text-secondary hover:bg-gray-200 transition-colors font-medium">Clear</button>
          </div>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste or type your text here…"
          className="w-full h-48 border border-gray-200 rounded-2xl px-4 py-3 text-sm text-text-primary resize-none focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: 'Words', value: stats.words.toLocaleString(), color: 'text-brand-blue' },
          { label: 'Characters', value: stats.characters.toLocaleString(), color: 'text-text-primary' },
          { label: 'No Spaces', value: stats.charactersNoSpaces.toLocaleString(), color: 'text-text-primary' },
          { label: 'Sentences', value: stats.sentences.toLocaleString(), color: 'text-text-primary' },
          { label: 'Paragraphs', value: stats.paragraphs.toLocaleString(), color: 'text-text-primary' },
          { label: 'Reading Time', value: stats.readingTime, color: 'text-green-600' },
        ].map((stat) => (
          <div key={stat.label} className="bg-surface rounded-xl p-4 text-center">
            <p className={`text-xl font-bold ${stat.color} mb-1`}>{stat.value}</p>
            <p className="text-xs text-text-muted">{stat.label}</p>
          </div>
        ))}
      </div>

      {text.length > 0 && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-sm text-blue-800">
            <span className="font-semibold">{stats.words}</span> words · <span className="font-semibold">{stats.characters}</span> characters · estimated reading time: <span className="font-semibold">{stats.readingTime}</span>
          </p>
        </div>
      )}
    </div>
  );
}
